/**
 * React glue around {@link AnalyticsClient}:
 *   - Exposes the singleton via the `useAnalytics()` hook.
 *   - Fires `session_open` once when auth resolves (per browser session).
 *   - Auto-emits `page_view` on every route change.
 *   - Drains the buffer on `visibilitychange:hidden` and `beforeunload`
 *     using `keepalive: true` so we don't lose the in-flight event
 *     batch when the player tabs away or closes the page.
 *
 * Skip-rules:
 *   - `/debug/render` bypasses chrome (no AuthProvider). The provider
 *     isn't mounted there, so no events fire — correct: the headless
 *     render harness shouldn't pollute production analytics.
 */

import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsClient, getAnalyticsClient, type EmitInput } from './analyticsClient';
import { useAuth } from '../auth/AuthProvider';

interface AnalyticsContextValue {
  emit(input: EmitInput): void;
  /**
   * Drain every queued event right now. Used by the abandon path to
   * make sure a song_abandon emit doesn't sit in the buffer behind a
   * provider-level drain that may have already run for this unload.
   */
  flushAll(reason?: 'unload' | 'manual'): Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

const SESSION_OPEN_KEY = 'gd-analytics-session-opened';

export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error('useAnalytics must be used inside <AnalyticsProvider>');
  }
  return ctx;
}

/**
 * Test-only escape hatch: lets unit tests provide a custom
 * {@link AnalyticsClient} (e.g. one with a mocked fetch). Production
 * code never passes `client`; the singleton is used.
 */
interface AnalyticsProviderProps {
  children: ReactNode;
  client?: AnalyticsClient;
}

export function AnalyticsProvider({ children, client }: AnalyticsProviderProps) {
  const resolvedClient = useMemo(() => client ?? getAnalyticsClient(), [client]);
  const { status, user } = useAuth();
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'ready' || !user) {
      return;
    }
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (sessionStorage.getItem(SESSION_OPEN_KEY)) {
      return;
    }
    sessionStorage.setItem(SESSION_OPEN_KEY, '1');
    resolvedClient.emit({
      eventType: 'session_open',
      props: {
        is_anonymous: user.isAnonymous,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : null,
        timezone:
          typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
        screen_width: typeof window !== 'undefined' ? window.innerWidth : null,
        screen_height: typeof window !== 'undefined' ? window.innerHeight : null,
      },
    });
  }, [status, user, resolvedClient]);

  // Page-view auto-tracking. Compares against the previous path so a
  // re-render with the same location doesn't emit twice (StrictMode
  // double-effects, query-param tweaks on the same page, etc.).
  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    const current = `${location.pathname}${location.search}`;
    if (previousPathRef.current === current) {
      return;
    }
    const previous = previousPathRef.current;
    previousPathRef.current = current;
    resolvedClient.emit({
      eventType: 'page_view',
      props: {
        path: location.pathname,
        search: location.search.slice(0, 256) || null,
        // Strip the query string from `previous_path` so an Auth
        // redirect with a fat `state` token can't blow the server's
        // per-prop 512-char cap and 400 the whole batch.
        previous_path: previous ? previous.split('?')[0] : null,
      },
    });
  }, [status, location.pathname, location.search, resolvedClient]);

  // Unload + tab-hide flush. `visibilitychange -> hidden` is the most
  // reliable signal across mobile and desktop — `beforeunload` doesn't
  // fire on mobile backgrounding. Both call into the same drain.
  useEffect(() => {
    const drain = () => {
      void resolvedClient.flushAll('unload');
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        drain();
      }
    };
    window.addEventListener('beforeunload', drain);
    window.addEventListener('pagehide', drain);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', drain);
      window.removeEventListener('pagehide', drain);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [resolvedClient]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      emit: (input) => resolvedClient.emit(input),
      flushAll: (reason = 'manual') => resolvedClient.flushAll(reason),
    }),
    [resolvedClient],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}
